// analytics/hooks/useTabFilters.js
// Unified filter state management hook for all tabs

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAppContext } from '../context/AppContext.jsx';

/**
 * Custom hook for managing tab filters with configuration sync
 * @param {Object} defaultFilters - Default filter values specific to the tab
 * @param {Object} options - Additional options
 * @returns {Object} Filter state and handlers
 */
export const useTabFilters = (defaultFilters = {}, options = {}) => {
    const { state, withManualImagesPref } = useAppContext();
    const { defaultDateRange, default_date_range } = state.preferences;
    
    // Use the configuration's default date range, with fallback
    const configDateRange = defaultDateRange || default_date_range || 'week';
    
    console.log('[useTabFilters] Configuration date range:', configDateRange);
    
    // Initialize filters with configuration-aware defaults
    const initialFilters = useMemo(() => ({
        dateRange: configDateRange,
        project: '',
        station: '',
        lotId: '',
        sortBy: 'last_run_at',
        statusFilter: '',
        ...defaultFilters
    }), [configDateRange, defaultFilters]);
    
    const [filters, setFilters] = useState(initialFilters);
    const [hasInitialized, setHasInitialized] = useState(false);
    
    // Sync with configuration changes (but only for dateRange if user hasn't manually changed it)
    useEffect(() => {
        if (!hasInitialized) {
            console.log('[useTabFilters] Initial sync with config:', configDateRange);
            setFilters(prev => ({
                ...prev,
                dateRange: configDateRange
            }));
            setHasInitialized(true);
        }
    }, [configDateRange, hasInitialized]);
    
    // Filter change handler
    const handleFilterChange = useCallback((key, value) => {
        console.log('[useTabFilters] Filter change:', key, '=', value);
        setFilters(prev => {
            const newFilters = { ...prev, [key]: value };
            
            // Reset dependent filters
            if (key === 'project') {
                newFilters.lotId = '';
            }
            
            return newFilters;
        });
        
        // Call page reset if provided
        if (options.onPageReset) {
            options.onPageReset();
        }
    }, [options]);
    
    // Reset filters to defaults
    const resetFilters = useCallback(() => {
        console.log('[useTabFilters] Resetting filters to defaults');
        setFilters(initialFilters);
        if (options.onPageReset) {
            options.onPageReset();
        }
    }, [initialFilters, options]);
    
    // Get filters with manual images preference applied
    const getConfiguredFilters = useCallback(() => {
        return withManualImagesPref ? withManualImagesPref(filters) : filters;
    }, [filters, withManualImagesPref]);
    
    // Memoized filter options for dependent filters
    const filterDependencies = useMemo(() => ({
        hasProject: !!filters.project,
        hasStation: !!filters.station,
        isAllTime: filters.dateRange === 'all'
    }), [filters.project, filters.station, filters.dateRange]);
    
    return {
        filters,
        handleFilterChange,
        resetFilters,
        getConfiguredFilters,
        filterDependencies,
        configDateRange,
        hasInitialized
    };
};